<script>
import LayoutEditor from '@layouts/editor.vue'
import { mapState, mapActions } from 'vuex'
import PixelPresence from '@components/embed/PixelPresence/PixelPresence.vue'
import CampaignConfigForm from '@components/forms/CampaignConfigForm.vue'
import CampaignDomainSetupForm from '@components/forms/CampaignDomainSetupForm.vue'
import CampaignScheduleForm from '@components/forms/CampaignScheduleForm.vue'
import CampaignSaveButton from '@components/forms/CampaignSaveButton.vue'
import CampaignIntegrations from '@components/campaignSetup/Integrations.vue'
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '@meta/campaignConstants'

export default {
  page: {
    title: 'Campaign Setup',
    meta: [{ name: 'description', content: 'CampaignSetup' }],
  },
  name: 'SetupView',
  components: {
    LayoutEditor,
    'pixel-presence-component': PixelPresence,
    'campaign-config-form-component': CampaignConfigForm,
    'campaign-domain-setup-form-component': CampaignDomainSetupForm,
    'campaign-schedule-form-component': CampaignScheduleForm,
    'campaign-save-button': CampaignSaveButton,
    'campaign-integrations': CampaignIntegrations,
  },
  data() {
    return {
      campaignTypes: CAMPAIGN_TYPE,
      saveEnabled: false,
      updatedCampaign: {},
    }
  },
  computed: {
    ...mapState('campaign', [
      'isCampaignLoaded',
      'campaignPartialSaveCompleted',
      'campaignPartialSaveError',
      'campaignActionErrorMessage',
    ]),
    campaign() {
      return this.$store.getters['campaign/targetCampaign']
    },
    targetWidgetID() {
      return this.$store.state.campaign.widget.targetWidgetID
    },
    isCampaignActive() {
      return this.campaign.status ? this.campaign.status === CAMPAIGN_STATUS.ACTIVE : false
    },
    isCampaignArchived() {
      return this.campaign.status ? this.campaign.status === CAMPAIGN_STATUS.ARCHIVED : false
    },
  },
  created() {
    this.checkEmbedCodePresence()
  },
  methods: {
    ...mapActions('campaign', ['partialSaveCampaign']),
    ...mapActions('campaign/embedCode', ['checkEmbedCodePresence']),
    ...mapActions('campaign/widget', ['setTargetWidget']),
    ...mapActions('integration', ['listIntegrations']),
    publishProject(goLive = true) {
      if (!this.isCampaignActive && !this.isCampaignArchived && goLive) {
        if (this.campaign.domainID === '') {
          this.$message({
            type: 'error',
            message: 'Your campaign is missing a domain. Please select a domain to proceed.',
          })
        } else {
          this.$msgbox
              .confirm(
                  'By clicking \'Confirm\' your campaign will go Live!',
                  'One more step',
                  {
                    type: 'info',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                  },
              )
              .then(() => {
                this.partialSaveCampaign({ status: CAMPAIGN_STATUS.ACTIVE }).then((response) => {
                      if (this.campaignPartialSaveCompleted) {
                        this.$message({
                          type: 'success',
                          message: 'Your campaign is now active',
                        })
                      } else if (this.campaignPartialSaveError) {
                        this.$message({
                          type: 'error',
                          message: this.campaignActionErrorMessage,
                        })
                      }
                    },
                )
              }).catch(() => {})
        }
      } else {
        this.$msgbox
            .confirm(
                'By clicking \'Confirm\' your campaign will be Unpublished!',
                'Unpublish campaign?',
                {
                  type: 'info',
                  confirmButtonText: 'Confirm',
                  cancelButtonText: 'Cancel',
                },
            )
            .then(() => {
              this.partialSaveCampaign({ status: CAMPAIGN_STATUS.INACTIVE }).then((response) => {
                    if (this.campaignPartialSaveCompleted) {
                      this.$message({
                        type: 'success',
                        message: 'Your campaign is now inactive',
                      })
                    } else if (this.campaignPartialSaveError) {
                      this.$message({
                        type: 'error',
                        message: this.campaignActionErrorMessage,
                      })
                    }
                  },
              )
            }).catch(() => {})
      }
    },
    updateCampaign(data) {
      Object.assign(this.updatedCampaign, data)
      this.saveEnabled = true
    },
    triggerChangeTargetWidget(widgetID) {
      this.setTargetWidget(widgetID).then(() => {
        this.listIntegrations()
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('campaign/CAMPAIGN_CLEAR')
    this.$store.commit('campaign/widget/WIDGETS_CLEAR')
    this.$store.commit('campaign/contentItem/CONTENT_ITEM_CLEAR')
    next()
  },
}
</script>

<template>
  <LayoutEditor>
    <el-row id="setup-view" :class="$style.setupView">
      <div :class="$style.setupNav">
        <campaign-save-button v-if="saveEnabled"
                              class="mr-2"
                              :campaign-partial="updatedCampaign"
                              @save-success="saveEnabled = false">
        </campaign-save-button>

        <template v-if="!isCampaignActive && !isCampaignArchived">
          <button class="btn contrast big"
                  :class="$style.btnPublishShare"
                  type="button"
                  @click="publishProject">
            <div class="flex">
              <svg class="h-auto mr-2 w-4" xmlns="http://www.w3.org/2000/svg" width="32" height="32"
                   viewBox="0 0 48 48">
                <g fill="none" stroke="currentColor" stroke-width="4">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="m20.906 6.063l1.43-.954a3 3 0 0 1 3.328 0l1.43.954A20 20 0 0 1 36 22.703V33H12V22.704a20 20 0 0 1 8.906-16.641"/>
                  <circle cx="24" cy="20" r="6"/>
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="m12 22l-6 6.217V33h36v-4.783L36 22M24 38v6m-8-4v2m16-2v2"/>
                </g>
              </svg>
              Publish
            </div>
          </button>
        </template>

        <template v-else>
          <template v-if="!isCampaignArchived">
            <button class="mr-3 btn white outline big"
                    type="button"
                    @click="publishProject(false)">
              Unpublish
            </button>
          </template>

          <router-link
              :to="{ name: 'dashboard' }"
              tag="button"
              type="primary"
              :class="$style.btnDashboard"
              class="btn contrast big">
            To Dashboard
          </router-link>
        </template>

      </div>

      <div class="container">
        <el-row>
          <el-col :span="16">
            <h1 class="has-subheading">Final Setup</h1>
            <p>Manage the campaign settings</p>
          </el-col>
          <template v-show="isCampaignLoaded">
            <el-col :span="8" :class="$style.campaignStatus">
              <h3 :class="{ [$style.green]: isCampaignActive }">
                {{ campaign.status ? campaign.status.toUpperCase() : '' }}
              </h3>
              <el-tooltip
                  content="Status of your current campaign"
                  placement="top-start">
                <i class="el-icon-info"></i>
              </el-tooltip>
            </el-col>
          </template>
        </el-row>

        <template v-if="isCampaignLoaded">
          <section class="mb-4 flex flex-col">
            <h3>Campaign</h3>
            <div class="py-2 my-4 flex flex-col space-x-4 bg-white border">
              <campaign-config-form-component class="px-4 pb-2" @update="updateCampaign"/>
            </div>
          </section>

          <section class="mb-4 flex flex-col">
            <h3>Website
              <el-popover
                  v-if="campaign.domainID === ''"
                  class="ml-2"
                  placement="top"
                  trigger="hover"
                  :content="'Your campaign is missing a domain'">
                <strong slot="reference">
                  <i class="el-icon-warning text-base text-onvocado-red"></i>
                </strong>
              </el-popover>
            </h3>
            <div class="py-2 my-4 flex flex-col space-x-4 bg-white border">
              <campaign-domain-setup-form-component class="px-4 pb-2" @update="updateCampaign"/>
            </div>
          </section>

          <section class="mb-4 flex flex-col">
            <div class="flex justify-between">
              <h3>Integrations</h3>
              <div v-if="campaign.type === campaignTypes.AB">
                <el-select
                    :value="targetWidgetID"
                    class="w-full"
                    default-first-option
                    @change="triggerChangeTargetWidget">
                  <el-option
                      v-for="widget in campaign.widgetItems"
                      :key="widget.id"
                      :label="widget.name"
                      :value="widget.id"></el-option>
                </el-select>
              </div>
            </div>
            <div class="py-2 my-4 flex flex-col space-x-4 bg-white border">
              <campaign-integrations class="px-4 pb-2"/>
            </div>
          </section>

          <section class="mb-4 flex flex-col">
            <h3>Schedule</h3>
            <div class="py-2 my-4 flex flex-col space-x-4 bg-white border">
              <campaign-schedule-form-component class="px-4 pb-2" @update="updateCampaign"/>
            </div>
          </section>

          <section class="mb-4 flex flex-col">
            <h3>Embed</h3>
            <div class="py-2 my-4">

              <pixel-presence-component :target-campaign-i-d="campaign.id"
                                        :hide-embed-details-if-found="true"
                                        :show-f-a-q="true">
              </pixel-presence-component>
            </div>
          </section>
        </template>

        <template v-else>
          <Loader/>
        </template>
      </div>
    </el-row>
  </LayoutEditor>
</template>

<style lang="scss" module>
@import '@design';

.setupView {
  padding: 0 20px;
}

.badge {
  margin-right: 15px;

  sup {
    right: 2px !important;
  }
}

.green {
  @apply text-onvocado-green;
}

.campaignStatus {
  margin-top: 70px;
  line-height: 40px;
  text-align: right;

  h3 {
    display: inline-block;
  }

  i {
    @apply text-xs text-onvocado-gray;

    padding-right: 20px;
    vertical-align: top;
  }
}

.btnPublishShare {
  margin-right: -20px;
}

.btnDashboard {
  margin-right: -20px;
}

.setupNav {
  @apply fixed top-0 z-20;

  right: 40px;
  height: 70px;
  line-height: 70px;
}
</style>
